import React from 'react'
import { cn } from '@/utils/cn'
import { IconPlus } from '@tabler/icons-react'
import { TeeButton } from './tee-button'

export interface TeeSectionFooterProps {
  /**
   * Action button text
   */
  actionLabel?: string
  
  /**
   * Action button click handler
   */
  onAction?: () => void
  
  /**
   * Custom icon for the action button
   */
  actionIcon?: React.ReactNode
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Secondary action button text
   */
  secondaryActionLabel?: string
  
  /**
   * Secondary action button click handler
   */
  onSecondaryAction?: () => void

  showActionIcon?: boolean

  /**
   * Disables the primary action. Optional and defaulting to false, so existing
   * callers are unaffected.
   */
  actionDisabled?: boolean
}

export const TeeSectionFooter = React.forwardRef<HTMLDivElement, TeeSectionFooterProps>(
  (
    {
      actionLabel,
      onAction,
      actionIcon = <IconPlus size={20} />,
      secondaryActionLabel,
      onSecondaryAction,
      className,
      showActionIcon = true,
      actionDisabled = false,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Sticks to the bottom of the scroll area so the primary action stays
          // reachable on long forms instead of requiring a scroll to the end.
          // `bottom-0` with safe-area padding keeps it clear of the home
          // indicator; z-20 puts it over form content but under sheets (z-70).
          //
          // MERGE NOTE: main grew its own mobile treatment for this footer
          // (`flex-col-reverse` + full-width buttons). This branch's sticky bar
          // with side-by-side buttons supersedes it; main's 44px touch target
          // is kept below.
          'w-full bg-[var(--background-card)] flex gap-3 md:gap-6 items-center justify-end p-4 md:p-6 rounded-md shadow-[0px_0px_15px_0px_rgba(0,0,0,0.3)]',
          'sticky bottom-0 z-20 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] md:pb-6',
          className
        )}
      >
        {secondaryActionLabel && (
          <TeeButton
            onClick={onSecondaryAction}
            variant="secondary"
            size="s"
            text={secondaryActionLabel}
            className="flex-1 min-h-[44px] md:flex-none md:min-h-0 md:min-w-[150px]"
          />
        )}

        {actionLabel && (
          <TeeButton
            onClick={onAction}
            variant="primary"
            size="s"
            leftIcon={showActionIcon ? actionIcon : undefined}
            text={actionLabel}
            className="flex-1 min-h-[44px] md:flex-none md:min-h-0 md:min-w-[150px]"
            disabled={actionDisabled}
          />
        )}
      </div>
    )
  }
)

TeeSectionFooter.displayName = 'TeeSectionFooter'

export { TeeSectionFooter as default }
